import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useToast } from '../components/ui/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionsAPI } from '../services/api';
import SupportAssistant from '../components/SupportAssistant';
import { analytics } from '../lib/ga4';
import { 
  CheckCircle, Zap, Shield, Clock, Brain, AlertTriangle,
  FileText, Scan, TestTube, BarChart3, Users, Headphones,
  Lock, ChevronDown, ChevronUp, Loader2, ArrowRight, Tag, CreditCard,
  RefreshCcw
} from 'lucide-react';

const PricingPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  // Track pricing page view
  useEffect(() => {
    analytics.trackPricingView();
  }, []);

  const handleValidatePromo = async (planId) => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await subscriptionsAPI.validatePromo(promoCode, planId);
      setPromoResult(res.data);
      toast({ title: 'Code promo valide !', description: res.data.description });
    } catch (err) {
      setPromoResult(null);
      toast({ title: 'Code invalide', description: err.response?.data?.detail || 'Code promo non reconnu', variant: 'destructive' });
    }
    setPromoLoading(false);
  };

  const handleSubscribe = async (planId) => {
    if (planId === 'free') {
      analytics.trackTrialStart('Essai Gratuit');
      navigate(isAuthenticated ? '/dashboard' : '/register');
      return;
    }

    if (!isAuthenticated) {
      toast({
        title: 'Connexion requise',
        description: 'Veuillez vous connecter pour souscrire',
      });
      navigate('/login?redirect=/pricing');
      return;
    }

    // Track checkout begin
    const planPrices = { starter: 14, pro: 29, expert: 49 };
    analytics.trackCheckoutBegin(planId, planPrices[planId] || 0);

    setLoadingPlan(planId);
    try {
      const response = await subscriptionsAPI.createCheckout(planId, window.location.origin, promoCode || null);
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Une erreur est survenue',
        variant: 'destructive'
      });
      setLoadingPlan(null);
    }
  };

  const valueProps = [
    {
      icon: Clock,
      title: "Gagnez du temps",
      description: "Automatisation complète de la conformité. Plus de saisie manuelle.",
      color: "text-blue-600",
      bg: "bg-blue-50"
    },
    {
      icon: CheckCircle,
      title: "Évitez les rejets DGFiP",
      description: "Validation en temps réel avant tout envoi. Zéro mauvaise surprise.",
      color: "text-emerald-600",
      bg: "bg-emerald-50"
    },
    {
      icon: Brain,
      title: "Comprenez simplement",
      description: "Explications claires à chaque étape. Pas besoin d'être expert.",
      color: "text-amber-600",
      bg: "bg-amber-50"
    }
  ];

  const plans = [
    {
      id: "free",
      name: "Découverte",
      subtitle: "Sans CB · pour démarrer",
      price: "0€",
      period: "/mois",
      popular: false,
      features: [
        { icon: FileText, text: "5 factures / mois", highlight: true },
        { icon: Shield, text: "Format Factur-X conforme 2026" },
        { icon: Scan, text: "Scanner IA (3 scans/mois)" },
        { icon: TestTube, text: "Export PDF & CSV" },
        { icon: Headphones, text: "Support email" },
      ],
      cta: "Commencer gratuitement",
      ctaVariant: "outline",
      note: "Aucune carte bancaire requise"
    },
    {
      id: "starter",
      name: "Starter",
      subtitle: "Idéal pour démarrer",
      price: "14€",
      period: "/mois",
      popular: false,
      features: [
        { icon: FileText, text: "20 factures / mois", highlight: true },
        { icon: Shield, text: "Format Factur-X conforme" },
        { icon: Scan, text: "Scanner IA (5 scans/mois)" },
        { icon: TestTube, text: "Simulation PPF/PDP" },
        { icon: Headphones, text: "Support email" },
      ],
      cta: "Choisir Starter",
      ctaVariant: "outline",
      note: "Pour auto-entrepreneurs"
    },
    {
      id: "pro",
      name: "Pro",
      subtitle: "Le plus populaire",
      price: "29€",
      period: "/mois",
      popular: true,
      features: [
        { icon: FileText, text: "Factures illimitées", highlight: true },
        { icon: Shield, text: "Format Factur-X (PDF/A-3 + XML)" },
        { icon: Scan, text: "Scanner IA illimité" },
        { icon: TestTube, text: "Simulation PPF/PDP complète" },
        { icon: BarChart3, text: "Score conformité 0-100%" },
        { icon: AlertTriangle, text: "Alertes et corrections guidées" },
        { icon: Headphones, text: "Support prioritaire" },
      ],
      cta: "Passer à Pro",
      ctaVariant: "primary",
      note: "Le choix des TPE/PME"
    },
    {
      id: "expert",
      name: "Expert",
      subtitle: "Pour les équipes",
      price: "49€",
      period: "/mois",
      popular: false,
      comingSoon: false,
      features: [
        { icon: FileText, text: "Tout Pro inclus", highlight: true },
        { icon: Users, text: "Jusqu'à 5 utilisateurs", highlight: true },
        { icon: Headphones, text: "Support prioritaire", highlight: true },
        { icon: FileText, text: "Modèles illimités" },
        { icon: BarChart3, text: "Import clients CSV" },
        { icon: CreditCard, text: "e-Reporting TVA (sept 2026)" },
      ],
      cta: "Choisir Expert",
      ctaVariant: "default",
      note: "Lancement -20% : 39€/mois",
      promoPrice: "39"
    }
  ];

  const faqs = [
    {
      question: "Puis-je tester sans risque ?",
      answer: "Absolument. Le plan Découverte est gratuit, sans carte bancaire, sans limite de durée. Vous créez jusqu'à 5 factures par mois pour tester l'ensemble des fonctionnalités essentielles."
    },
    {
      question: "Comment fonctionne la garantie 30 jours ?",
      answer: "Si vous n'êtes pas satisfait pendant les 30 premiers jours suivant votre paiement, il vous suffit de nous contacter via le support. Nous procédons au remboursement intégral sous 48h, sans justificatif ni question. C'est aussi simple que ça."
    },
    {
      question: "Que comprend le plan Découverte ?",
      answer: "Le plan Découverte vous permet de créer 5 factures par mois au format Factur-X conforme 2026, d'utiliser le scanner IA jusqu'à 3 fois par mois, et d'exporter vos données en PDF et CSV. Le tout gratuitement, sans carte bancaire."
    },
    {
      question: "Suis-je conforme sans expert-comptable ?",
      answer: "Oui. SmartFacture AI vérifie automatiquement la conformité de vos factures selon les normes 2026 (SIREN, TVA, format Factur-X). Des explications claires vous guident à chaque étape."
    },
    {
      question: "Que se passe-t-il quand j'atteins la limite mensuelle ?",
      answer: "Si vous dépassez 5 factures sur le plan Découverte (ou 20 sur le Starter), vous êtes invité à passer au plan supérieur. Aucune surprise, aucun prélèvement automatique : c'est vous qui décidez."
    }
  ];

  return (
    <div className="min-h-screen" style={{backgroundColor: '#FAFFF7'}}>
      {/* Navigation */}
      <nav className="border-b border-emerald-100 sticky top-0 z-50 bg-white/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src="/smartfacture-logo.png" alt="SmartFacture AI" className="h-9 w-auto" />
            </Link>
            <div className="flex items-center space-x-3">
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 rounded-xl text-white">
                    Tableau de bord
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-slate-600 hover:text-slate-800">
                      Connexion
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-emerald-500 hover:bg-emerald-600 rounded-xl text-white">
                      Créer un compte
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <section className="pt-16 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* Official Compliance Badge */}
          <div className="mb-8 flex justify-center">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-2xl shadow-sm">
              <div className="flex items-center justify-center w-10 h-10 bg-white rounded-xl shadow-sm border border-emerald-100">
                <Shield className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="text-left">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-emerald-700">Conforme e-facturation 2026</span>
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                </div>
                <span className="text-xs text-slate-500">Opérateur de Dématérialisation · Factur-X</span>
              </div>
            </div>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
            La conformité e-facturation 2026,
            <br />
            <span className="text-emerald-600">simple et sans stress</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Factures électroniques, simulation PPF/PDP et relances automatiques 
            dans une plateforme claire et sécurisée.
          </p>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            {valueProps.map((prop, idx) => {
              const Icon = prop.icon;
              return (
                <Card key={idx} className="bg-white border-0 shadow-sm rounded-2xl">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 ${prop.bg} rounded-xl flex items-center justify-center mb-4`}>
                      <Icon className={`h-6 w-6 ${prop.color}`} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">{prop.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{prop.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
            {plans.map((plan, idx) => (
              <Card 
                key={idx}
                className={`relative border-0 shadow-sm rounded-2xl overflow-hidden transition-all duration-300 flex flex-col ${
                  plan.popular 
                    ? 'ring-2 ring-blue-600 shadow-lg shadow-blue-100 xl:scale-[1.03]' 
                    : plan.earlyAdopterLink
                      ? 'ring-2 ring-amber-400 shadow-lg shadow-amber-100'
                      : 'hover:shadow-md'
                }`}
                style={{backgroundColor: '#ffffff'}}
                data-testid={`plan-${plan.id}`}
              >
                {plan.popular && (
                  <div className="bg-emerald-500 text-white text-center py-2.5 text-sm font-semibold tracking-wide">
                    Recommandé
                  </div>
                )}
                {plan.earlyAdopterLink && (
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center py-2.5 text-sm font-semibold tracking-wide">
                    Early Adopter -20%
                  </div>
                )}
                
                <CardContent className={`p-6 flex flex-col flex-1 ${plan.popular || plan.earlyAdopterLink ? '' : 'pt-7'}`}>
                  {/* Plan Header */}
                  <div className="mb-4">
                    <h3 className="text-xl font-bold mb-1" style={{color: '#1e293b'}}>{plan.name}</h3>
                    <p className="text-sm" style={{color: '#64748b'}}>{plan.subtitle}</p>
                  </div>
                  
                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold" style={{fontFamily: "'Outfit', sans-serif", color: '#1e293b'}}>{plan.price}</span>
                      <span className="ml-1.5 text-base" style={{color: '#64748b'}}>{plan.period}</span>
                    </div>
                  </div>
                  
                  {/* Features */}
                  <div className="space-y-3.5 mb-8 flex-1">
                    {plan.features.map((feature, fidx) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div key={fidx} className="flex items-center space-x-3">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            feature.highlight ? 'bg-emerald-100' : 'bg-slate-100'
                          }`}>
                            <FeatureIcon className={`h-3.5 w-3.5 ${
                              feature.highlight ? 'text-emerald-600' : 'text-slate-500'
                            }`} />
                          </div>
                          <span className={`text-sm leading-snug ${
                            feature.highlight ? 'font-semibold' : ''
                          }`} style={{color: feature.highlight ? '#1e293b' : '#475569'}}>
                            {feature.text}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* CTA Button */}
                  {plan.earlyAdopterLink ? (
                    <Link to={plan.earlyAdopterLink} className="block">
                      <Button 
                        className="w-full h-12 rounded-xl font-semibold text-sm bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md"
                        data-testid={`subscribe-btn-${plan.id}`}
                      >
                        {plan.cta}
                        <ArrowRight className="h-4 w-4 ml-1.5" />
                      </Button>
                    </Link>
                  ) : (
                    <Button 
                      className={`w-full h-12 rounded-xl font-semibold text-sm ${
                        plan.comingSoon
                          ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                          : plan.ctaVariant === 'primary' 
                            ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-md shadow-emerald-500/30' 
                            : plan.ctaVariant === 'outline'
                              ? 'bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-blue-300'
                              : 'bg-slate-800 hover:bg-slate-900 text-white'
                      }`}
                      onClick={() => !plan.comingSoon && handleSubscribe(plan.id)}
                      disabled={loadingPlan === plan.id || plan.comingSoon}
                      data-testid={`subscribe-btn-${plan.id}`}
                    >
                      {loadingPlan === plan.id ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          {plan.cta}
                          {!plan.comingSoon && <ArrowRight className="h-4 w-4 ml-1.5" />}
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Note */}
                  <p className="text-center text-sm mt-3" style={{color: '#94a3b8'}}>{plan.note}</p>
                  
                  {/* Guarantee Badge - Only for paid plans */}
                  {plan.id !== 'free' && (
                    <div className="flex items-center justify-center gap-1.5 mt-3 text-emerald-600">
                      <CheckCircle className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">Sans engagement – 30 jours satisfait ou remboursé</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Promo Code Section */}
      <section className="pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl" data-testid="promo-code-section">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="h-5 w-5 text-emerald-600" />
                <h3 className="text-sm font-semibold text-slate-800">Vous avez un code promo ?</h3>
              </div>
              <div className="flex gap-2">
                <Input 
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Ex: LANCEMENT26"
                  className="border-slate-200 text-sm uppercase"
                  data-testid="promo-code-input"
                />
                <Button 
                  onClick={() => handleValidatePromo('pro')}
                  disabled={promoLoading || !promoCode.trim()}
                  variant="outline"
                  className="border-emerald-200 text-emerald-600 hover:bg-emerald-50 px-4"
                  data-testid="promo-code-validate"
                >
                  {promoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Appliquer'}
                </Button>
              </div>
              {promoResult && (
                <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="promo-code-result">
                  <p className="text-sm text-emerald-700 font-medium">{promoResult.description}</p>
                  <p className="text-xs text-emerald-600 mt-1">
                    Premier mois uniquement. Non cumulable.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 30-Day Money Back Guarantee Section */}
      <section className="pb-10 px-4 sm:px-6 lg:px-8" data-testid="guarantee-section">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-gradient-to-br from-emerald-50 to-teal-50/50 border border-emerald-200 shadow-sm rounded-2xl overflow-hidden">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                {/* Icon */}
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-200">
                  <RefreshCcw className="h-8 w-8 text-white" />
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-slate-800 mb-2">
                    Garantie 30 jours satisfait ou remboursé
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Testez SmartFacture AI en toute confiance. Si vous n'êtes pas satisfait dans les 30 premiers jours, 
                    nous vous remboursons intégralement, sans question.
                  </p>
                  
                  {/* Trust Points */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-3 w-3 text-emerald-600" />
                      </div>
                      <span className="text-sm text-slate-700">Remboursement simple</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-3 w-3 text-emerald-600" />
                      </div>
                      <span className="text-sm text-slate-700">Aucune condition cachée</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-3 w-3 text-emerald-600" />
                      </div>
                      <span className="text-sm text-slate-700">Support réactif</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Policy & Legal Summary */}
      <section className="pb-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Card className="bg-gradient-to-br from-emerald-50/40 to-white border border-emerald-100 shadow-sm rounded-2xl">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">
                    Essai gratuit disponible
                  </h3>
                  <p className="text-sm text-slate-600 mb-3 leading-relaxed">
                    Scannez simplement vos documents : notre intelligence artificielle extrait, classe et génère 
                    automatiquement vos factures et données comptables. Aucune configuration complexe, aucune saisie manuelle.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600">Accès immédiat à toutes les fonctionnalités</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600">Annulation possible à tout moment</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600">Garantie satisfait ou remboursé 30 jours</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    L'abonnement est sans engagement et peut être annulé à tout moment. La garantie de remboursement 
                    s'applique pendant les 30 premiers jours suivant le premier paiement.{' '}
                    <Link to="/cgu" className="text-emerald-600 hover:underline font-medium">Voir les CGU complètes</Link>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Legal Reassurance */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Card className="bg-white border-0 shadow-sm rounded-2xl">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Lock className="h-7 w-7 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    Vos données restent privées
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    Aucune facture n'est transmise à l'administration sans votre validation explicite. 
                    Le mode simulation vous permet de tester en toute sécurité.
                  </p>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-slate-100 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <Shield className="h-5 w-5 text-slate-400 mx-auto mb-2" />
                  <p className="text-xs text-slate-500">Données chiffrées</p>
                </div>
                <div className="text-center">
                  <CheckCircle className="h-5 w-5 text-slate-400 mx-auto mb-2" />
                  <p className="text-xs text-slate-500">Conforme RGPD</p>
                </div>
                <div className="text-center">
                  <Lock className="h-5 w-5 text-slate-400 mx-auto mb-2" />
                  <p className="text-xs text-slate-500">Serveurs en France</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Questions fréquentes
          </h2>
          
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <Card 
                key={idx} 
                className="bg-white border-0 shadow-sm rounded-xl overflow-hidden cursor-pointer"
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
              >
                <CardContent className="p-0">
                  <div className="flex items-center justify-between p-5">
                    <h3 className="text-base font-medium text-slate-800 pr-4">
                      {faq.question}
                    </h3>
                    {openFaq === idx ? (
                      <ChevronUp className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    )}
                  </div>
                  
                  {openFaq === idx && (
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-t border-emerald-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
            Prêt à simplifier votre conformité 2026 ?
          </h2>
          <p className="text-slate-600 mb-6">
            Rejoignez les entreprises qui préparent sereinement la réforme e-facturation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Button 
              className="bg-emerald-500 hover:bg-emerald-600 h-12 px-8 rounded-full text-base text-white shadow-lg shadow-emerald-500/30"
              onClick={() => handleSubscribe('free')}
            >
              Essayer gratuitement
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Link to="/">
              <Button variant="outline" className="h-12 px-8 rounded-xl text-base border-slate-200">
                En savoir plus
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-50/50 border-t border-emerald-100 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <div className="flex items-center">
              <img src="/smartfacture-logo.png" alt="SmartFacture AI" className="h-7 w-auto" />
            </div>
            <p className="text-sm text-slate-500">
              © 2026 SmartFacture AI. Conformité e-facturation 2026.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Help Button */}
      <SupportAssistant />
    </div>
  );
};

export default PricingPage;
